import PropTypes from 'prop-types';
import { useState, useMemo } from 'react';
// @mui
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import Table from '@mui/material/Table';
import Divider from '@mui/material/Divider';
import TableRow from '@mui/material/TableRow';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import CardHeader from '@mui/material/CardHeader';
import Card from '@mui/material/Card';
import TableContainer from '@mui/material/TableContainer';
import CardContent from "@mui/material/CardContent";
import { Stack } from '@mui/system';
import { Button, InputAdornment, ListSubheader, MenuItem, TextField } from '@mui/material';
// utils
import { fCurrencyEuro } from '../../utils/format-number';
// components
import Label from '../../components/label';
import { TableHeadCustom } from '../../components/table';
import Iconify from '../../components/iconify';

// ----------------------------------------------------------------------

const AGRUPACION_OPTIONS = {
  Abono: ['DURO', 'BLANDO', 'MOVIMIENTOS', 'EXTRAORDINARIOS'],
  Gasto: ['BÁSICO', 'DISCRECIONAL', 'MOVIMIENTOS', 'EXTRAORDINARIOS'],
};

export default function FinancialTransactionsTable({ title, subheader, tableData, tableLabels, ...other }) {
  const [filterConcepto, setFilterConcepto] = useState('');
  const [filterStartDate, setFilterStartDate] = useState(null);
  const [filterEndDate, setFilterEndDate] = useState(null);
  const [filterCategoria, setFilterCategoria] = useState('');
  const [filterDetalle, setFilterDetalle] = useState('');
  const [filterAgrupacion, setFilterAgrupacion] = useState('');

  const dataFilteredByGlobal = useMemo(() => 
    tableData.filter((row) => {
      const fecha = new Date(row.value_date);
      const concepto = row.concepts?.toLowerCase() || '';
      
      const matchConcepto = concepto.includes(filterConcepto.toLowerCase());
      const matchStart = filterStartDate ? fecha >= new Date(filterStartDate) : true;
      const matchEnd = filterEndDate ? fecha <= new Date(filterEndDate) : true;

      return matchConcepto && matchStart && matchEnd;
    }),[tableData, filterConcepto, filterStartDate, filterEndDate]);

  const dataFilteredByAgrupacion = useMemo(() => {
    if (!filterAgrupacion) return dataFilteredByGlobal;

    return dataFilteredByGlobal.filter((row) => {
      const amount = Number(row.amount || 0);
      const [tipo, nombre] = filterAgrupacion.split(':');
      
      let tipoMatch = false;
      if (tipo === 'ABONO') tipoMatch = amount > 0;
      else if (tipo === 'GASTO') tipoMatch = amount <= 0;

      let nombreMatch = false;

      if (nombre === 'EXTRAORDINARIOS') {
        if (tipo === 'ABONO') {
          const extraAbonos = ['FINANCIACIÓN', 'IMPUESTOS'];
          nombreMatch = extraAbonos.includes(row.agrupacion);
        } else {
          const extraGastos = ['FINANCIACIÓN', 'RECOBRO', 'EMBARGO'];
          nombreMatch = extraGastos.includes(row.agrupacion);
        }
      } else {
        nombreMatch = row.agrupacion === nombre;
      }

      return nombreMatch && tipoMatch;
    });
  }, [dataFilteredByGlobal, filterAgrupacion]);

  const dataFilteredByCategoria = useMemo(() => {
    if (!filterCategoria) return dataFilteredByAgrupacion;
    return dataFilteredByAgrupacion.filter((row) => row.categoria === filterCategoria);
  }, [dataFilteredByAgrupacion, filterCategoria]);

  const finalFilteredData = useMemo(() => {
    let data = dataFilteredByCategoria;
    
    if (filterDetalle) {
      data = data.filter((row) => row.detalle === filterDetalle);
    }

    return [...data].sort((a, b) => new Date(b.value_date) - new Date(a.value_date));

  }, [dataFilteredByCategoria, filterDetalle]);


  const categoriasDisponibles = useMemo(() => {
    const cats = dataFilteredByAgrupacion.map((row) => row.categoria).filter(Boolean);
    return [...new Set(cats)].sort((a, b) => a.localeCompare(b));
  }, [dataFilteredByAgrupacion]);

  
  const detallesDisponibles = useMemo(() => {
    const dets = dataFilteredByCategoria.map((row) => row.detalle).filter(Boolean);
    return [...new Set(dets)].sort((a, b) => a.localeCompare(b));
  }, [dataFilteredByCategoria]);


  const handleClearFilters = () => {
    setFilterConcepto('');
    setFilterStartDate(null);
    setFilterEndDate(null);
    setFilterAgrupacion('');
    setFilterCategoria('');
    setFilterDetalle('');
  };

  const handleAgrupacionChange = (e) => {
    setFilterAgrupacion(e.target.value);
    setFilterCategoria('');
    setFilterDetalle('');
  };

  const handleCategoriaChange = (e) => {
    setFilterCategoria(e.target.value);
    setFilterDetalle('');
  };


  return (
    <Card {...other}>
      {title && (<CardHeader title={title} subheader={subheader} />)}

      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            select
            label="Agrupación"
            value={filterAgrupacion}
            onChange={handleAgrupacionChange}
            fullWidth
            size="small"
            sx={{ maxWidth: { md: 200 } }}
          >
            <MenuItem value="">Todas</MenuItem>
            
            <ListSubheader sx={{ fontWeight: 'bold', color: 'success.main' }}>
              Ingresos
            </ListSubheader>
            {AGRUPACION_OPTIONS.Abono.map((opcion) => (
              <MenuItem key={`ABONO:${opcion}`} value={`ABONO:${opcion}`}>
                {opcion}
              </MenuItem>
            ))}

            <Divider sx={{ borderStyle: 'dashed' }} />

            <ListSubheader sx={{ fontWeight: 'bold', color: 'error.main' }}>
              Gastos
            </ListSubheader>
            {AGRUPACION_OPTIONS.Gasto.map((opcion) => (
              <MenuItem key={`GASTO:${opcion}`} value={`GASTO:${opcion}`}>
                {opcion}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Categoría"
            value={filterCategoria}
            onChange={handleCategoriaChange}
            fullWidth
            size="small"
            sx={{ maxWidth: { md: 300 } }}
            disabled={!filterAgrupacion && categoriasDisponibles.length === 0}
          >
            <MenuItem value="">Todas</MenuItem>
            {categoriasDisponibles.map((cat) => (
              <MenuItem key={cat} value={cat}>
                {cat}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Detalle"
            value={filterDetalle}
            onChange={(e) => setFilterDetalle(e.target.value)}
            fullWidth
            size="small"
            sx={{ maxWidth: { md: 300 } }}
            disabled={!filterCategoria && detallesDisponibles.length === 0}
          >
            <MenuItem value="">Todas</MenuItem>
            {detallesDisponibles.map((det) => (
              <MenuItem key={det} value={det}>
                {det}
              </MenuItem>
            ))}
          </TextField>

          <DatePicker
            label="Fecha inicio"
            value={filterStartDate}
            onChange={(newValue) => setFilterStartDate(newValue)}
            slotProps={{
              textField: { fullWidth: true, size: 'small' },
              field: { clearable: true }, // 👈 para limpiar
            }}
            sx={{ maxWidth: { md: 170 } }}
          />

          <DatePicker
            label="Fecha fin"
            value={filterEndDate}
            onChange={(newValue) => setFilterEndDate(newValue)}
            slotProps={{
              textField: { fullWidth: true, size: 'small' },
              field: { clearable: true }, // 👈 para limpiar
            }}
            sx={{ maxWidth: { md: 170 } }}
          />

          <TextField
            label="Buscar concepto"
            value={filterConcepto}
            onChange={(e) => setFilterConcepto(e.target.value)}
            fullWidth
            size="small"
            sx={{ maxWidth: { md: 500 } }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify icon="eva:search-fill" sx={{ color: 'text.disabled' }} />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            color="primary"
            onClick={handleClearFilters}
            sx={{ minWidth: 150 }}
          >
            Limpiar filtros
          </Button>
        </Stack>

        <TableContainer style={{ maxHeight: 600, minHeight: 400 }}>
          <Table sx={{ minWidth: 600 }} stickyHeader>
            <TableHeadCustom headLabel={tableLabels} />
            <TableBody>
              {finalFilteredData.map((row) => (
                <FinancialTransactionTableRow key={row.id} row={row} />
              ))}
              {finalFilteredData.length === 0 && (
                 <TableRow>
                   <TableCell colSpan={8} align="center">
                     No se encontraron transacciones
                   </TableCell>
                 </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </CardContent>

      <Divider sx={{ borderStyle: 'dashed' }} />
    </Card>
  );
}

FinancialTransactionsTable.propTypes = {
  subheader: PropTypes.string,
  tableData: PropTypes.array,
  tableLabels: PropTypes.array,
  title: PropTypes.string,
};

// ----------------------------------------------------------------------

function FinancialTransactionTableRow({ row }) {

  return (
      <TableRow>
        <TableCell>
          {new Intl.DateTimeFormat('es-MX', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            timeZone: 'UTC'
          }).format(new Date(row.value_date))}
        </TableCell>
        <TableCell>
          <Label
            variant="soft"
            color={
              row.amount > 0 ? "success" : "error"
            }
          >
            {fCurrencyEuro(row.amount || '0')}
          </Label>
        </TableCell>
        <TableCell>
            <Label
              variant="soft"
              color="warning"
            >
              {fCurrencyEuro(row.balance_final || '0')}
            </Label>
          </TableCell>
        <TableCell>{row.concepts}</TableCell>
        <TableCell>{row.detalle}</TableCell>
        <TableCell>{row.categoria}</TableCell>
        <TableCell>{row.agrupacion}</TableCell>
        <TableCell>{row.metodo}</TableCell>
      </TableRow>
  );
}

FinancialTransactionTableRow.propTypes = {
    row: PropTypes.object,
};
